import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import { useAuth } from '../../contexts/AuthContext';

const StudentTour = () => {
  const { user } = useAuth();
  const location = useLocation();
  const startedRef = useRef(false);
  const didDriveRef = useRef(false);

  useEffect(() => {
    if (!user || user.role !== 'student') return;
    if (location.pathname !== '/dashboard') return;
    
    // Only show tour on first login
    if (!user.first_login) {
      console.log('🎯 Not first login, skipping tour');
      return;
    }

    // Prevent multiple starts on re-renders while the tour is active.
    if (startedRef.current) return;
    startedRef.current = true;

    console.log('🎯 Initializing student tour for first-time user:', user.email || user.id);

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

    const steps = [
      ...(isMobile
        ? [
          {
            element: '[data-tour="menu-toggle"]',
            popover: {
              title: 'Navigation menu',
              description: 'Tap this to open the sidebar and follow the tour.',
              side: 'bottom',
              align: 'start',
            },
          },
        ]
        : []),
      {
        element: '[data-tour="main-content"]',
        popover: {
          title: 'Welcome',
          description:
            'This is your student dashboard. Here’s a quick tour of the main sections.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-dashboard"]',
        popover: {
          title: 'Dashboard',
          description: 'Your overview: progress, stats, and quick actions.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-courses"]',
        popover: {
          title: 'Courses',
          description: 'Browse and continue your courses from here.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-live-classes"]',
        popover: {
          title: 'Live classes',
          description: 'Join upcoming live sessions and view schedules.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-recordings"]',
        popover: {
          title: 'Recordings',
          description: 'Watch class recordings and take notes.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-tests"]',
        popover: {
          title: 'Tests / Quiz',
          description: 'Attempt quizzes and view results.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-assignments"]',
        popover: {
          title: 'Assignments',
          description: 'View assignments and submit your work.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-codelab"]',
        popover: {
          title: 'CodeLab',
          description: 'Practice coding with interactive labs.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-bookmarks"]',
        popover: {
          title: 'Bookmarks',
          description: 'Save content to revisit later.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="nav-profile"]',
        popover: {
          title: 'Profile',
          description: 'Update your profile details and preferences.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="top-profile"]',
        popover: {
          title: 'Account menu',
          description: 'Access your profile and log out from here.',
          side: 'bottom',
          align: 'end',
        },
      },
    ];

    const driverObj = driver({
      showProgress: true,
      allowClose: true,
      steps,
    });

    const timer = setTimeout(() => {
      try {
        // Check if elements exist before starting tour
        const firstElement = document.querySelector(steps[0]?.element);
        if (!firstElement) {
          console.log('🚫 Tour elements not ready, skipping tour');
          return;
        }
        
        didDriveRef.current = true;
        console.log('🎯 Starting student tour...');
        driverObj.drive();
      } catch (error) {
        console.error('❌ Tour error:', error);
        // If elements aren't available yet, don't block the app.
      }
    }, 1000); // Increased delay to 1 second

    return () => {
      clearTimeout(timer);
      try {
        driverObj.destroy();
      } catch {
        // ignore
      }
    };
  }, [location.pathname, user]);

  return null;
};

export default StudentTour;
